import { Component, ElementRef, OnInit, ViewChild } from '@angular/core'
import { FormControl, FormGroup } from '@angular/forms'
import { B2CConfig } from '@inside-hub-app/customer-portal-config'
import {
  SharedService,
  TransifexService
} from '@inside-hub-app/customer-portal-shared'
import { TranslationService } from '@emilfreydigital/transifex-angular'
import { EfRemoteConfigurationService } from '@inside-hub-app/ef-remote-config'
import { KcDataService } from '../../../kc-data.service'
import { KcConsentsViewData } from '../../../interfaces/kc-data.interface'

@Component({
  selector: 'customer-portal-revolution-consents',
  templateUrl: './revolution-consents.component.html'
})
export class RevolutionKcConsentsComponent implements OnInit {
  country: string

  dataProtectionLinks = {
    de: null,
    it: null,
    fr: null
  }

  constructor(
    private readonly kcDataService: KcDataService,
    private readonly sharedService: SharedService,
    private readonly transifexTranslationsService: TranslationService,
    public transifexService: TransifexService,
    private readonly remoteConfigService: EfRemoteConfigurationService<B2CConfig>
  ) {
    const lang = this.sharedService.currentLanguage()
    this.privacyLink = {
      url: String(this.dataProtectionLinks[lang]),
      translation: ''
    }
    this.country = this.remoteConfigService.get('country.code')
    this.dataProtectionLinks = {
      de: this.remoteConfigService.get('footerLinks.dataProtectionLinks.de'),
      it: this.remoteConfigService.get('footerLinks.dataProtectionLinks.it'),
      fr: this.remoteConfigService.get('footerLinks.dataProtectionLinks.fr')
    }
    this.consentsLinks = this.remoteConfigService.get('consentsLinks.email')
  }

  @ViewChild('consentsForm', { static: false })
  private readonly formRef: ElementRef

  public form = new FormGroup({})

  public viewData: KcConsentsViewData
  public consentName: string
  public channels: string[]
  public selectedChannels: string[]
  public submitting: boolean
  public emailLink = ''
  public consentLink = ''
  public newGdprNotice = false
  privacyLink: {
    translation: string
    url: string
  }

  consentsLinks: string
  ngOnInit(): void {
    this.newGdprNotice = this.remoteConfigService.get('country.code') === 'de'
    this.emailLink =
      '<a href="mailto:' +
      this.consentsLinks +
      '">' +
      this.consentsLinks +
      '</a>'
    this.viewData = this.kcDataService.getViewData() as KcConsentsViewData
    this.consentName = this.viewData.groupConsentName
    this.channels = JSON.parse(this.viewData.groupConsentChannels ?? null)
    this.channels.forEach(channel => {
      this.form.addControl(channel, new FormControl<boolean | null>(false))
    })

    this.form.valueChanges.subscribe(value => {
      this.selectedChannels = []
      for (const key in value) {
        if (value[key]) {
          this.selectedChannels.push(key)
        }
      }

      // workaround for 2 inputs using same control
      // https://github.com/angular/angular/issues/13792
      this.form.setValue(value, { emitEvent: false })
    })
  }

  submit(): void {
    this.submitting = true
    // submit form
    const formElement = this.formRef.nativeElement as HTMLFormElement
    formElement.submit()
  }

  setPrivacyLink(): void {
    const privacy =
      this.transifexService.getSharedTranslation('data-protection')

    const lang = this.sharedService.currentLanguage()
    this.privacyLink = {
      url: String(this.dataProtectionLinks[lang]),
      translation: privacy
    }

    this.consentLink =
      '<a target="_blank" href="' +
      String(this.dataProtectionLinks[lang]) +
      '">' +
      this.privacyLink.translation +
      '</a>'
  }

  getTranslation1(): string {
    return this.transifexTranslationsService.translate(
      '<b>Categories of personal data:</b> We use the voluntarily provided contact data such as e-mail and telephone and, if necessary, other information such as interesting brand, hobbies, age, etc. for advertising and marketing purposes of the car dealership and/or the others below Approved recipients for personalized advertising offers and marketing campaigns such as sweepstakes, premium car-related events personalized to your interests and market research surveys via our contract service providers.',
      {
        _key: 'customerPortal.kc.communication.gdpr.personal-data-categories',
        _tags: 'kc-forms, 3.1'
      }
    )
  }

  getTranslation2(): string {
    return this.transifexTranslationsService.translate(
      '<b>Purposes:</b> For advertising purposes, in particular to make information about products, offers and services, invitations to product presentations and test drives more interesting for you and to select them based on your data, etc.) and for market research purposes, including Satisfaction surveys to improve our products and services. For this purpose, we will evaluate and analyze the personal data provided in order to be able to offer you a customer-specific approach and tailor-made offers and to continuously improve our customer service.',
      {
        _key: 'customerPortal.kc.communication.gdpr.purposes',
        _tags: 'kc-forms, 3.1'
      }
    )
  }

  getTranslation3(): string {
    return this.transifexTranslationsService.translate(
      'For advertising purposes, invitations to product presentations and test drives as well as for market research purposes, including satisfaction surveys. For this purpose, the personal data provided are evaluated and analyzed in order to be able to offer you a customer-specific approach and tailored offers.',
      {
        _key: 'customerPortal.kc.consents.gdpr-notice1',
        _tags: 'kc-forms, 3.1',
        email: this.emailLink
      }
    )
  }

  getTranslation4(): string {
    return this.transifexTranslationsService.translate(
      'The declaration of consent can be revoked or changed at any time for the future and without giving reasons. Simply send a message to {email} (e.g. with the email you have given us).',
      {
        _key: 'customerPortal.kc.consents.gdpr-notice2',
        _tags: 'kc-forms, 3.1',
        email: this.emailLink
      }
    )
  }

  getTranslation5(): string {
    this.setPrivacyLink()
    return this.transifexTranslationsService.translate(
      'Further information on data protection and your rights as a data subject can be found in the {link}.',
      {
        _key: 'customerPortal.kc.consents.gdpr-notice3',
        _tags: 'kc-forms, 3.1',
        link: this.consentLink
      }
    )
  }

  getTranslation6(): string {
    return this.transifexService.getSharedTranslation('data-protection')
  }
}
